import { Fragment, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, Outlet, useNavigate } from "react-router";

import type { TenantMembership } from "@re-platform/api-client";
import { useActingTenant } from "../lib/actingTenant";
import { useMemberships } from "../lib/memberships";
import { api, loadAdminToken, saveAdminToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRoles, type StaffRole } from "../lib/roles";

interface NavItem {
  to: string;
  label: string;
  /** Roles allowed to see this entry — mirrors the backend require_role gate. */
  allow: StaffRole[];
  /**
   * Brokerage types this entry applies to; undefined = every type. Used for
   * screens that are meaningful on only one side of the business (a TREC rate
   * card means nothing to a mortgage brokerage). Gating is cosmetic — the route
   * stays reachable by deep link, and an unknown type fails OPEN (shows the
   * item) so a slow memberships fetch never hides a link the user does have.
   */
  brokerageTypes?: string[];
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const ALL_STAFF: StaffRole[] = ["super_admin", "brokerage_admin", "agent"];

/**
 * The people who actually work with clients.
 *
 * A super_admin operates the PLATFORM, not a book of business: they belong to
 * the platform tenant, which has no clients of its own, so every one of these
 * screens came up empty for them and read as broken. Client work belongs to the
 * brokerage that owns the relationship.
 */
const BROKERAGE_STAFF: StaffRole[] = ["brokerage_admin", "agent"];

/**
 * The admin nav, grouped into collapsible sections, gated to mirror each
 * screen's backend role gate (so staff never see a link that would 403).
 * A group renders only if the role can see at least one of its items;
 * expanded/collapsed state persists per browser (localStorage).
 */
const NAV: readonly NavEntry[] = [
  { to: "/", label: "Dashboard", end: true, allow: ALL_STAFF },
  {
    label: "Clients",
    items: [
      // Agents need Clients too: the "Invite a client" panel is agent-only
      // server-side (the invite records THEM as the client's agent). Caught at
      // Checkpoint #9 — the panel had shipped on an admin-gated screen.
      { to: "/clients", label: "Clients", allow: BROKERAGE_STAFF },
      // Client property-viewing requests (Phase 11); server-side scoping keeps
      // agents to their own.
      {
        to: "/appointments",
        label: "Showing requests",
        allow: BROKERAGE_STAFF,
      },
      // Voice-assistant followups; server-side scoping keeps agents to requests
      // routed to them.
      {
        to: "/requests",
        label: "Requests & questions",
        allow: BROKERAGE_STAFF,
      },
      // Placeholder screen (feature TBD) — real route so the nav link isn't dead.
      {
        to: "/document-review",
        label: "Document review",
        allow: BROKERAGE_STAFF,
      },
    ],
  },
  {
    label: "Forms",
    items: [
      // Sent forms and Active dossiers are a BOOK OF BUSINESS — agreements sent
      // to real clients and the dossiers tracking them. The platform tenant owns
      // none, so a super_admin got empty screens that read as broken; that is
      // the case BROKERAGE_STAFF above already describes.
      { to: "/forms", label: "Sent forms", allow: BROKERAGE_STAFF },
      {
        to: "/forms/templates",
        label: "Form templates",
        // NOT a book of business, despite sitting between two that are. The
        // real PDFs sent for signature: a brokerage's own, an agent's own
        // (V073, scoped by owner_agent_id), and the PLATFORM-STANDARD catalog
        // that every brokerage forks from. trec-svc scopes a super_admin's list
        // with set_platform_context, so this screen shows them that catalog
        // rather than nothing — and `_assert_can_edit` refuses a platform row
        // to anyone else ("Only the platform can map a standard template"), so
        // this is the ONLY place the baseline can be maintained. Dropping
        // super_admin here once left it with no UI at all.
        allow: ALL_STAFF,
      },
      {
        // Platform defaults again, same reasoning as Form templates above.
        to: "/dossiers/templates",
        label: "Dossier templates",
        allow: ["super_admin", "brokerage_admin"],
      },
      {
        to: "/dossiers/active",
        label: "Active dossiers",
        allow: BROKERAGE_STAFF,
      },
    ],
  },
  {
    label: "Brokerage",
    items: [
      {
        // Widest-to-narrowest audience: everyone has a brokerage, only the
        // platform has tenants, and Staff sits with the admins who manage it.
        //
        // An AGENT sees this read-only (the screen renders no inputs for them,
        // and brokerage-settings refuses the write regardless). The identity and
        // fee defaults here are what their own agreements are written on, so
        // "what does my brokerage say on my paperwork" should not require asking
        // their broker.
        to: "/brokerage",
        label: "My brokerage",
        allow: ALL_STAFF,
      },
      { to: "/tenants", label: "Tenants", allow: ["super_admin"] },
      {
        to: "/staff",
        label: "Staff",
        allow: ["super_admin", "brokerage_admin"],
      },
    ],
  },
  {
    label: "Billing",
    items: [
      { to: "/billing/margin", label: "Margin", allow: ["super_admin"] },
      // Broker-facing: recharge heavy agents (own tenant); super sees any tenant.
      {
        to: "/billing/agent-usage",
        label: "Agent usage",
        allow: ["super_admin", "brokerage_admin"],
      },
      {
        to: "/billing/subscriptions",
        label: "Subscriptions",
        allow: ["super_admin"],
      },
      { to: "/billing/plans", label: "Billing plans", allow: ["super_admin"] },
      {
        to: "/billing/invoices",
        label: "Generate invoices",
        allow: ["super_admin"],
      },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/profile", label: "My account", allow: ALL_STAFF },
      { to: "/my-usage", label: "My usage", allow: ["agent"] },
      // The agent's own TXR-1501 terms (V061) — a TREC form, so hidden for a
      // mortgage brokerage (the route stays as a deep-link fallback).
      {
        to: "/rate-card",
        label: "My rate card",
        allow: ["agent"],
        brokerageTypes: ["REAL_ESTATE"],
      },
    ],
  },
];

const ROLE_TITLE: Record<string, string> = {
  super_admin: "Platform Admin",
  brokerage_admin: "Broker Dashboard",
  agent: "Agent Dashboard",
};

/** localStorage key holding the labels of the groups the user collapsed. */
const COLLAPSED_KEY = "rp-admin-nav-collapsed";

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((v): v is string => typeof v === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

/**
 * Which brokerage the nav should gate against: the explicitly-chosen acting
 * tenant when dual-licensed, else the only membership. Undefined while the
 * fetch is in flight, or when a multi-brokerage user has not picked one —
 * callers must treat undefined as "show everything".
 */
function useBrokerageType(rows: TenantMembership[]): string | undefined {
  // Context, not localStorage — the nav must re-gate on an in-app switch (PR 4).
  const { acting } = useActingTenant();
  if (acting) return acting.brokerageType;
  return rows.length === 1 ? rows[0]?.brokerage_type : undefined;
}

function NavLinkItem({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => (isActive ? "active" : "")}
    >
      {item.label}
    </NavLink>
  );
}

export function Shell() {
  const { allows, primary } = useRoles();
  const title = (primary ? ROLE_TITLE[primary] : null) ?? "Dashboard";

  // Sidebar collapse / responsive drawer. Open by default on wide screens,
  // hidden (hamburger only) on narrow ones; the top-bar hamburger toggles it
  // either way, and it auto-collapses/expands when crossing the breakpoint.
  const NARROW = 900;
  const [navOpen, setNavOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth > NARROW,
  );
  useEffect(() => {
    let wasWide = window.innerWidth > NARROW;
    const onResize = () => {
      const isWide = window.innerWidth > NARROW;
      if (isWide !== wasWide) {
        setNavOpen(isWide);
        wasWide = isWide;
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const closeOnNarrow = () => {
    if (window.innerWidth <= NARROW) setNavOpen(false);
  };

  // Groups default EXPANDED; a collapse persists per browser so the nav opens
  // the way the user left it.
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);
  const toggle = (label: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // Private-mode storage failure only loses persistence, not the toggle.
      }
      return next;
    });

  const memberships = useMemberships();
  const brokerageType = useBrokerageType(memberships.data ?? []);

  // An item shows when the role allows it AND it applies to this brokerage
  // type. Unknown type ⇒ show (fail open) — see NavItem.brokerageTypes.
  const shows = (item: NavItem) =>
    allows(item.allow) &&
    (item.brokerageTypes === undefined ||
      brokerageType === undefined ||
      item.brokerageTypes.includes(brokerageType));

  // Gate items first; a group with nothing visible disappears entirely.
  const visible = NAV.flatMap<NavEntry>((entry) => {
    if ("items" in entry) {
      const items = entry.items.filter(shows);
      return items.length > 0 ? [{ ...entry, items }] : [];
    }
    return shows(entry) ? [entry] : [];
  });

  return (
    <div
      className={`rp-shell ${navOpen ? "rp-shell--nav-open" : "rp-shell--nav-closed"}`}
    >
      <div
        className="rp-shell__backdrop"
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />
      <aside className="rp-shell__sidebar">
        <h1 className="rp-shell__title">
          <span className="rp-shell__mark" aria-hidden="true">
            🏠
          </span>
          {title}
        </h1>
        <BrokerageSwitcher />
        <nav className="rp-shell__nav">
          {visible.map((entry) =>
            "items" in entry ? (
              <Fragment key={entry.label}>
                <button
                  type="button"
                  className="rp-navgroup__header"
                  onClick={() => toggle(entry.label)}
                  aria-expanded={!collapsed.has(entry.label)}
                >
                  <span className="rp-navgroup__label">{entry.label}</span>
                  <span className="rp-navgroup__toggle" aria-hidden="true">
                    {collapsed.has(entry.label) ? "+" : "−"}
                  </span>
                </button>
                <div
                  className={`rp-navgroup__items ${collapsed.has(entry.label) ? "is-collapsed" : ""}`}
                >
                  <div className="rp-navgroup__inner">
                    {entry.items.map((item) => (
                      <NavLinkItem
                        key={item.to}
                        item={item}
                        onNavigate={closeOnNarrow}
                      />
                    ))}
                  </div>
                </div>
              </Fragment>
            ) : (
              <NavLinkItem
                key={entry.to}
                item={entry}
                onNavigate={closeOnNarrow}
              />
            ),
          )}
        </nav>
        <SessionFooter />
      </aside>
      <main className="rp-shell__main">
        <TopBar onMenu={() => setNavOpen((o) => !o)} />
        <div className="rp-shell__content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function HomeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

/**
 * Sticky content-area top bar: hamburger + a Home shortcut on the left
 * (the sidebar already shows where you are, so this space returns you to the
 * dashboard), notifications + theme + the signed-in user on the right.
 */
function TopBar({ onMenu }: { onMenu: () => void }) {
  return (
    <header className="rp-topbar">
      <button
        type="button"
        className="rp-topbar__icon rp-topbar__menu"
        onClick={onMenu}
        aria-label="Toggle navigation"
      >
        ☰
      </button>
      <Link
        to="/"
        className="rp-topbar__icon rp-topbar__home"
        aria-label="Home"
        title="Home"
      >
        <HomeIcon />
      </Link>
      <div className="rp-topbar__spacer" />
      <div className="rp-topbar__actions">
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}

/** Signed-in identity chip + sign-out (Auth0 mode). */
function UserMenu() {
  const { user, mode, logout } = useAuth();
  const { primary } = useRoles();
  const name =
    user?.name ?? user?.email ?? (mode === "dev" ? "Dev session" : "Signed in");
  const roleLabel = primary ? (ROLE_TITLE[primary] ?? primary) : "Staff";
  const initial = (name[0] ?? "?").toUpperCase();
  const picture = user?.picture;
  return (
    <div className="rp-topbar__user">
      <div className="rp-topbar__avatar">
        {picture ? <img src={picture} alt={name} /> : initial}
      </div>
      <div className="rp-topbar__id">
        <div className="rp-topbar__name">{name}</div>
        <div className="rp-topbar__role">{roleLabel}</div>
      </div>
      {mode === "auth0" && (
        <button
          type="button"
          className="rp-btn rp-btn--secondary rp-btn--sm"
          onClick={logout}
        >
          Sign out
        </button>
      )}
    </div>
  );
}

/**
 * Notification bell — badge = NEW followup requests in the caller's scope
 * (GET /admin/notifications/summary; generic typed counts so later categories
 * ride the same endpoint). Polls every 60s; click goes to the Requests screen.
 */
interface NotificationsSummary {
  followups: { new: number };
  appointments?: { pending: number };
  /** SEC-12 — device signals with ≥3 signups in 7 days; admin-only, a fraud-review flag. */
  device_flags?: { flagged: number };
  total: number;
}

function NotificationBell() {
  const navigate = useNavigate();
  const summary = useQuery<NotificationsSummary>({
    queryKey: ["notifications-summary"],
    queryFn: () =>
      api.request<NotificationsSummary>("/admin/notifications/summary"),
    refetchInterval: 60_000,
    retry: false,
  });

  const total = summary.data?.total ?? 0;

  return (
    <button
      type="button"
      className="rp-topbar__icon"
      onClick={() => navigate("/requests")}
      title={
        total > 0
          ? `${total} new client request${total === 1 ? "" : "s"}`
          : "Client requests"
      }
      aria-label={`Client requests${total > 0 ? ` (${total} new)` : ""}`}
    >
      🔔
      {total > 0 && (
        <span className="rp-topbar__badge">{total > 99 ? "99+" : total}</span>
      )}
    </button>
  );
}

/**
 * Sidebar session footer. In Auth0 SSO mode it shows the signed-in staff user
 * + a logout button; in dev mode (no Auth0 env) it falls back to the token
 * paste field.
 */
function SessionFooter() {
  const { mode } = useAuth();
  // Auth0 identity + sign-out now live in the top bar (UserMenu); the sidebar
  // keeps only the dev token-paste for local dev without SSO.
  if (mode === "dev") return <DevToken />;
  return null;
}

/**
 * Brokerage indicator for dual-licensed staff.
 *
 * This used to be a segmented TOGGLE, because every screen showed exactly one
 * brokerage and switching was the only way to reach the other. Since PR 5 the
 * lists and dashboard MERGE both, with a Brokerage badge per row and each
 * action addressed to its own row's tenant — so there is nothing left to
 * toggle, and a control that silently halved what you could see would now be a
 * trap rather than a feature.
 *
 * What remains is informational: which brokerages you are seeing at once.
 * Renders nothing for single-brokerage staff.
 *
 * The acting-tenant machinery itself is NOT removed — per-row actions and the
 * create/invite pickers still use it; it just is not a global mode any more.
 */
function BrokerageSwitcher() {
  const memberships = useMemberships();
  const rows = memberships.data ?? [];
  if (rows.length <= 1) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      <span
        style={{
          display: "block",
          color: "var(--rp-color-text-muted)",
          fontSize: 11,
          marginBottom: 4,
        }}
      >
        Showing both brokerages
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {rows.map((m) => (
          <span
            key={m.tenant_id}
            title={m.display_name}
            style={{
              padding: "2px 8px",
              borderRadius: "var(--rp-radius-sm)",
              background: "var(--rp-color-surface-muted, #eee)",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--rp-color-text-muted)",
            }}
          >
            {m.brokerage_type === "MORTGAGE" ? "Mortgage" : "Real estate"}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Dev-only session entry (no Auth0 env): paste a backend session JWT
 * (scripts/mint-dev-session-jwt.py with super_admin / brokerage_admin role).
 */
function DevToken() {
  const [token, setToken] = useState(loadAdminToken() ?? "");
  return (
    <div style={{ marginTop: "auto", paddingTop: 16, fontSize: 12 }}>
      <label
        style={{
          display: "block",
          color: "var(--rp-color-text-muted)",
          marginBottom: 4,
        }}
      >
        Dev session token
      </label>
      <input
        type="password"
        value={token}
        placeholder="paste session JWT…"
        onChange={(e) => setToken(e.target.value)}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid var(--rp-color-border)",
          borderRadius: "var(--rp-radius-sm)",
          font: "inherit",
          fontSize: 12,
        }}
      />
      <button
        type="button"
        className="rp-btn rp-btn--secondary rp-btn--sm"
        style={{ marginTop: 6, width: "100%" }}
        onClick={() => {
          saveAdminToken(token.trim());
          // Reload so AuthProvider re-reads the token's role claim and the nav
          // + route guards reflect the new role.
          window.location.reload();
        }}
      >
        Save token
      </button>
    </div>
  );
}
